// import Modal from "components/ui/Modal";
// import DateInput from "./shared/DateInput.jsx";
// import { toCSV, ymd, getMonthRange, pickDate } from "../utils/attendanceUtils";
// import { useMemo, useState } from "react";

// export default function ExportModal({ open, onClose, allRows }) {
//   const [exportType, setExportType] = useState("current");
//   const [exportFrom, setExportFrom] = useState(ymd(new Date()));
//   const [exportTo,   setExportTo]   = useState(ymd(new Date()));
//   const [exporting,  setExporting]  = useState(false);

//   const exportRows = useMemo(() => {
//     if (exportType === "current") return allRows;

//     let range = null;
//     if (exportType === "thisMonth" || exportType === "lastMonth") range = getMonthRange(exportType);
//     if (exportType === "range") range = { from: exportFrom, to: exportTo };
//     if (!range?.from || !range?.to) return [];

//     return allRows.filter((r) => {
//       const dayIso = ymd(pickDate(r));
//       return dayIso && dayIso >= range.from && dayIso <= range.to;
//     });
//   }, [allRows, exportType, exportFrom, exportTo]);

//   const doExport = async () => {
//     try {
//       setExporting(true);
//       const csv = toCSV(exportRows);
//       const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
//       const url = URL.createObjectURL(blob);
//       const a = document.createElement("a");
//       a.href = url;
//       a.download = `attendance_${exportType}_${ymd(new Date())}.csv`;
//       a.click();
//       URL.revokeObjectURL(url);
//       onClose();
//     } catch (e) {
//       console.error("Export error:", e);
//       alert("Failed to export CSV.");
//     } finally {
//       setExporting(false);
//     }
//   };

//   return (
//     <Modal
//       open={open}
//       onClose={exporting ? undefined : onClose}
//       title="Export Attendance"
//       size="sm"
//     >
//       <div className="space-y-4">
//         <div className="space-y-3">
//           <label className="block text-sm font-medium dark:text-white">
//             Export Filter
//           </label>

//           <select
//             value={exportType}
//             onChange={(e) => setExportType(e.target.value)}
//             className="w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
//           >
//             <option value="current">Current Table Filter</option>
//             <option value="thisMonth">This Month</option>
//             <option value="lastMonth">Last Month</option>
//             <option value="range">Custom Date Range</option>
//           </select>

//           {exportType === "range" && (
//             <div className="grid grid-cols-2 gap-3">
//               <DateInput value={exportFrom} onChange={setExportFrom} />
//               <DateInput value={exportTo}   onChange={setExportTo} />
//             </div>
//           )}

//           <p className="text-sm text-gray-600 dark:text-white/80">
//             This will export{" "}
//             <span className="font-medium">{exportRows.length}</span> records.
//           </p>
//         </div>

//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             disabled={exporting}
//             onClick={onClose}
//             className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
//           >
//             Cancel
//           </button>

//           <button
//             type="button"
//             onClick={doExport}
//             disabled={exporting || exportRows.length === 0}
//             className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
//           >
//             {exporting ? "Downloading..." : "Download CSV"}
//           </button>
//         </div>
//       </div>
//     </Modal>
//   );
// }

// src/views/hrms/attendance/components/ExportModal.jsx
import { useMemo, useState } from "react";
import Modal     from "components/ui/Modal";
import DateInput from "./shared/DateInput.jsx";
import {
  toCSV, ymd, getMonthRange, pickDate, STATUS_CONFIG,
} from "../utils/attendanceUtils";

export default function ExportModal({ open, onClose, allRows }) {
  const [exportType, setExportType] = useState("current");
  const [exportFrom, setExportFrom] = useState(ymd(new Date()));
  const [exportTo,   setExportTo]   = useState(ymd(new Date()));
  const [exporting,  setExporting]  = useState(false);

  const exportRows = useMemo(() => {
    if (exportType === "current") return allRows;

    let range = null;
    if (exportType === "thisMonth" || exportType === "lastMonth") {
      range = getMonthRange(exportType);
    }
    if (exportType === "range") {
      range = { from: exportFrom, to: exportTo };
    }
    if (!range?.from || !range?.to) return [];

    return allRows.filter((r) => {
      const dayIso = ymd(pickDate(r));
      return dayIso && dayIso >= range.from && dayIso <= range.to;
    });
  }, [allRows, exportType, exportFrom, exportTo]);

  // Summary counts for preview
  const summary = useMemo(() => {
    const counts = {};
    Object.keys(STATUS_CONFIG).forEach((k) => { counts[k] = 0; });
    exportRows.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });
    return counts;
  }, [exportRows]);

  const doExport = async () => {
    try {
      setExporting(true);
      const csv  = toCSV(exportRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `attendance_${exportType}_${ymd(new Date())}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      console.error("Export error:", e);
      alert("Failed to export CSV.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={exporting ? undefined : onClose}
      title="Export Attendance"
      size="sm"
    >
      <div className="space-y-4">
        {/* Filter type */}
        <div className="space-y-3">
          <label className="block text-sm font-medium dark:text-white">
            Export Range
          </label>

          <select
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
          >
            <option value="current">Current Table Filter</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="range">Custom Date Range</option>
          </select>

          {exportType === "range" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">From</label>
                <DateInput value={exportFrom} onChange={setExportFrom} />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">To</label>
                <DateInput value={exportTo} onChange={setExportTo} />
              </div>
            </div>
          )}
        </div>

        {/* Summary preview */}
        <div className="rounded-lg border p-3 dark:border-white/10">
          <p className="mb-2 text-xs font-semibold text-gray-600 dark:text-white/70">
            Preview — {exportRows.length} records
          </p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) =>
              summary[key] > 0 ? (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className={`rounded-full px-2 py-0.5 font-medium ${cfg.bg} ${cfg.text}`}>
                    {cfg.label}
                  </span>
                  <span className="font-semibold dark:text-white">{summary[key]}</span>
                </div>
              ) : null
            )}
            {exportRows.length === 0 && (
              <p className="col-span-2 text-gray-400">No records in this range.</p>
            )}
          </div>
        </div>

        {/* CSV columns note */}
        <p className="text-xs text-gray-500 dark:text-white/50">
          CSV includes: Employee, Date, Check In/Out, Hours, Break, Status, Late By, Deduction, Range
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={exporting}
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-60 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={doExport}
            disabled={exporting || exportRows.length === 0}
            className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? "Downloading..." : `Download CSV (${exportRows.length})`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
